// src/components/UserContractView/components/HistorySection.jsx

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Collapse,
  Avatar,
} from '@mui/material';

import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  History as HistoryIcon,
  Launch as LaunchIcon,
  Description as DocumentIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

import { statusChipProps, dateFromSignature, buildPublicLink, copyToClipboard } from './contractUtils';
import { BLITZ_ROOT, FILTER_OPTIONS } from './contractConstants';

const HistorySection = ({ history, onOpenContract, onQuickPreview, loading }) => {
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState(FILTER_OPTIONS.ALL);

  const filteredHistory = history.filter(sig => {
    if (filter === FILTER_OPTIONS.ALL) return true;
    return sig.status === filter;
  });

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: 'success.main' }}>
              <HistoryIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">
                Contract History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {history.length} {history.length === 1 ? 'signature' : 'signatures'}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                displayEmpty
              >
                <MenuItem value={FILTER_OPTIONS.ALL}>All</MenuItem>
                <MenuItem value={FILTER_OPTIONS.SIGNED}>Signed</MenuItem>
                <MenuItem value={FILTER_OPTIONS.AGREED}>Agreed</MenuItem>
                <MenuItem value={FILTER_OPTIONS.DECLINED}>Declined</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          {filteredHistory.length === 0 ? (
            <Box textAlign="center" py={4}>
              <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                No contract history yet
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Contract</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredHistory.map((signature, idx) => {
                    if (!signature) return null;
                    const assignmentId = signature?.assignment?.id || signature?.assignment_id;
                    const assignment = signature?.assignment || {};
                    const template = assignment?.template || {};
                    const { color, icon } = statusChipProps(signature.status);
                    const publicLink = assignmentId ? buildPublicLink(assignmentId, BLITZ_ROOT) : null;

                    return (
                      <TableRow key={assignmentId || idx} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {template?.name || 'Contract'}
                          </Typography>
                          {signature.decline_reason && (
                            <Typography variant="caption" color="error.main" display="block">
                              Reason: {signature.decline_reason}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip icon={icon} label={signature.status} color={color} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(dateFromSignature(signature)).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Open">
                            <IconButton
                              size="small"
                              onClick={() => onOpenContract(assignmentId)}
                              disabled={!assignmentId}
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Preview">
                            <IconButton
                              size="small"
                              onClick={() => onQuickPreview(signature, true)}
                              disabled={!assignmentId}
                            >
                              <DocumentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {publicLink && (
                            <Tooltip title="Copy link">
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(publicLink)}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default HistorySection;